import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Spinner } from "react-bootstrap";
import Select from "react-select";
import axios from "../../context/AxiosInstance";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { FaFileContract, FaFileSignature } from "react-icons/fa";
import "../../styles/rh-modern-ui.css";

const MySwal = withReactContent(Swal);

const ContratFormModal = ({
  show,
  handleClose,
  isEditMode,
  selectedContrat,
  token,
  refreshData,
}) => {
  const [formData, setFormData] = useState({
    type_contrat: "",
    poste: "",
    service: "",
    departement: "",
    lieu: "",
    numero_cnss: "",
    banque: "",
    date_debut: "",
    date_fin: "",
    duree_annees: "",
    id_employe: "",
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [employeOptions, setEmployeOptions] = useState([]);
  const [loadingEmployes, setLoadingEmployes] = useState(false);
  const [departements, setDepartements] = useState([]);
  const [villes, setVilles] = useState([]);

  const typeOptions = [
    { value: "CDI", label: "CDI" },
    { value: "CDD", label: "CDD" },
    { value: "CONTRACTUEL", label: "CONTRACTUEL" },
    { value: "CONVENTION", label: "CONVENTION" },
    { value: "DECRET", label: "DECRET" },
    { value: "ARRETE", label: "ARRETE" },
    { value: "NOTE DE SERVICE", label: "NOTE DE SERVICE" },
  ];

  const normalizeDateInput = (val) => {
    if (!val) return "";
    if (typeof val === "string") {
      const m = val.match(/^\d{4}-\d{2}-\d{2}/);
      if (m) return m[0];
      const first = val.split(/[T\s]/)[0];
      if (/^\d{4}-\d{2}-\d{2}$/.test(first)) return first;
    }
    const d = new Date(val);
    if (!isNaN(d.getTime())) return d.toISOString().slice(0, 10);
    return "";
  };

  const toDateSafe = (val) => {
    if (!val) return null;
    if (typeof val === "string" && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
      const d = new Date(`${val}T00:00:00`);
      return isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(val);
    return isNaN(d.getTime()) ? null : d;
  };

  const calculerDuree = (debut, fin = null) => {
    const d1 = toDateSafe(debut);
    const d2 = toDateSafe(fin) || new Date();
    if (!d1 || !d2) return "";
    let totalMonths = (d2.getFullYear() - d1.getFullYear()) * 12 + (d2.getMonth() - d1.getMonth());
    if (d2.getDate() < d1.getDate()) totalMonths -= 1;
    if (totalMonths < 0) totalMonths = 0;
    const annees = Math.floor(totalMonths / 12);
    const mois = totalMonths % 12;
    return `${annees} an(s) et ${mois} mois`;
  };

  useEffect(() => {
    if (formData.date_debut) {
      const nouvelleDuree = calculerDuree(formData.date_debut, formData.date_fin);
      setFormData((prev) => ({
        ...prev,
        duree_annees: nouvelleDuree,
      }));
    }
  }, [formData.date_debut, formData.date_fin]);

  useEffect(() => {
    const loadEmployes = async () => {
      setLoadingEmployes(true);
      try {
        const res = await axios.get("/employes/", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const options = res.data.data.map((emp) => ({
          value: emp.id_employe,
          label: `${emp.nom} ${emp.prenom}`,
        }));
        setEmployeOptions(options);
        try {
          const [deps, vls] = await Promise.all([
            axios.get("/parametrage/departements"),
            axios.get("/parametrage/villes"),
          ]);
          setDepartements(Array.isArray(deps.data?.data) ? deps.data.data : []);
          setVilles(Array.isArray(vls.data?.data) ? vls.data.data : []);
        } catch {
          /* parametrage optionnel */
        }
      } catch (err) {
        MySwal.fire("Erreur 🔥", "Impossible de charger les employés", "error");
      } finally {
        setLoadingEmployes(false);
      }
    };
    if (show) loadEmployes();
  }, [show, token]);

  useEffect(() => {
    if (isEditMode && selectedContrat) {
      const nd = normalizeDateInput(selectedContrat.date_debut);
      const nf = normalizeDateInput(selectedContrat.date_fin);
      setFormData({
        type_contrat: selectedContrat.type_contrat || "",
        poste: selectedContrat.poste || "",
        service: selectedContrat.service || "",
        departement: selectedContrat.departement || "",
        lieu: selectedContrat.lieu || "",
        numero_cnss: selectedContrat.numero_cnss || "",
        banque: selectedContrat.banque || "",
        date_debut: nd,
        date_fin: nf,
        duree_annees: calculerDuree(nd, nf),
        id_employe: selectedContrat.id_employe || "",
      });
    } else {
      setFormData({
        type_contrat: "",
        poste: "",
        service: "",
        departement: "",
        lieu: "",
        numero_cnss: "",
        banque: "",
        date_debut: "",
        date_fin: "",
        duree_annees: "",
        id_employe: "",
      });
    }
    setErrors({});
  }, [show, isEditMode, selectedContrat]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.type_contrat) newErrors.type_contrat = "Type requis";
    if (!formData.date_debut) newErrors.date_debut = "Date de début requise";
    if (!formData.id_employe) newErrors.id_employe = "Employé requis";
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const valErrors = validate();
    if (Object.keys(valErrors).length > 0) {
      setErrors(valErrors);
      return;
    }

    setLoading(true);
    try {
      const config = { headers: { Authorization: `Bearer ${token}` } };
      if (isEditMode) {
        await axios.patch(
          `/contrats/${selectedContrat.id_contrat}`,
          formData,
          config
        );
        MySwal.fire("✅ Contrat modifié", "", "success");
      } else {
        await axios.post("/contrats/", formData, config);
        MySwal.fire("✅ Contrat ajouté", "", "success");
      }

      refreshData();
      handleClose();
    } catch (err) {
      const backend = err.response?.data?.detail;
      if (backend?.message) {
        MySwal.fire("Erreur ❌", backend.message, "error");
      } else if (typeof backend === "string") {
        MySwal.fire("Erreur ❌", backend, "error");
      } else {
        MySwal.fire("Erreur ❌", "Une erreur s'est produite", "error");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={handleClose} centered backdrop="static" className="rh-modern-modal" size="lg">
      <Modal.Header closeButton className="rh-modal-header rh-modal-header-contract">
        <div>
          <Modal.Title>
            {isEditMode ? (
              <><FaFileContract className="me-2" />Modifier un contrat</>
            ) : (
              <><FaFileSignature className="me-2" />Ajouter un contrat</>
            )}
          </Modal.Title>
          <div className="rh-modal-subtitle">
            {isEditMode ? "Ajustez les termes du contrat existant." : "Associez un employé et définissez les conditions du contrat."}
          </div>
        </div>
      </Modal.Header>
      <Form onSubmit={handleSubmit}>
        <Modal.Body className="rh-modal-body">
          <div className="rh-modal-section">
            <div className="rh-modal-section-title">Employé & type</div>
            <Form.Group className="mb-3">
              <Form.Label>Employé *</Form.Label>
              {loadingEmployes ? (
                <Spinner animation="border" size="sm" />
              ) : (
                <div className="rh-select-wrap">
                  <Select
                    options={employeOptions}
                    placeholder="Rechercher un employé…"
                    value={employeOptions.find((opt) => opt.value === formData.id_employe)}
                    onChange={(selected) =>
                      setFormData((prev) => ({ ...prev, id_employe: selected?.value }))
                    }
                    isClearable
                    className={errors.id_employe ? "is-invalid" : ""}
                  />
                </div>
              )}
              {errors.id_employe && (
                <div className="invalid-feedback d-block">{errors.id_employe}</div>
              )}
            </Form.Group>

            <Form.Group className="mb-0">
              <Form.Label>Type de contrat *</Form.Label>
              <Form.Select
                name="type_contrat"
                value={formData.type_contrat}
                onChange={handleChange}
                isInvalid={!!errors.type_contrat}
              >
                <option value="">-- Sélectionner --</option>
                {typeOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">{errors.type_contrat}</Form.Control.Feedback>
            </Form.Group>
          </div>

          <div className="rh-modal-section">
            <div className="rh-modal-section-title">Poste & affectation</div>
            {["poste", "service"].map((field) => (
              <Form.Group className="mb-3" key={field}>
                <Form.Label>{field.charAt(0).toUpperCase() + field.slice(1)}</Form.Label>
                <Form.Control
                  type="text"
                  name={field}
                  value={formData[field]}
                  onChange={handleChange}
                />
              </Form.Group>
            ))}

            <Form.Group className="mb-3">
              <Form.Label>Département</Form.Label>
              <Form.Select name="departement" value={formData.departement} onChange={handleChange}>
                <option value="">-- Sélectionner --</option>
                {departements.map((d) => (
                  <option key={d.id_departement} value={d.nom}>{d.nom}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Ville</Form.Label>
              <Form.Select name="lieu" value={formData.lieu} onChange={handleChange}>
                <option value="">-- Sélectionner --</option>
                {villes.map((v) => (
                  <option key={v.id_ville} value={v.nom}>{v.nom}</option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Numéro d&apos;immatriculation CNSS</Form.Label>
              <Form.Control
                type="text"
                name="numero_cnss"
                value={formData.numero_cnss}
                onChange={handleChange}
                placeholder="Ex: CNSS-12345678"
              />
            </Form.Group>

            <Form.Group className="mb-0">
              <Form.Label>Banque</Form.Label>
              <Form.Control
                type="text"
                name="banque"
                value={formData.banque}
                onChange={handleChange}
                placeholder="Ex: Ecobank Guinée"
              />
            </Form.Group>
          </div>

          <div className="rh-modal-section">
            <div className="rh-modal-section-title">Dates & durée</div>
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Date début *</Form.Label>
                  <Form.Control
                    type="date"
                    name="date_debut"
                    value={formData.date_debut}
                    onChange={handleChange}
                    isInvalid={!!errors.date_debut}
                  />
                  <Form.Control.Feedback type="invalid">{errors.date_debut}</Form.Control.Feedback>
                </Form.Group>
              </div>
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Date fin</Form.Label>
                  <Form.Control
                    type="date"
                    name="date_fin"
                    value={formData.date_fin}
                    onChange={handleChange}
                  />
                </Form.Group>
              </div>
            </div>

            <Form.Group className="mb-0">
              <Form.Label>Durée calculée</Form.Label>
              <Form.Control type="text" value={formData.duree_annees} readOnly className="bg-light" />
            </Form.Group>
          </div>
        </Modal.Body>

        <Modal.Footer className="rh-modal-footer">
          <Button variant="outline-secondary" onClick={handleClose}>
            Annuler
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? (
              <Spinner animation="border" size="sm" />
            ) : isEditMode ? (
              "Enregistrer"
            ) : (
              "Ajouter"
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default ContratFormModal;
